import { useEffect, useState, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { BellIcon, CheckCircleIcon, ClockIcon, EyeIcon } from '@heroicons/react/24/outline'
import { useNotifications } from '../domains/notifications/NotificationContext'

export default function NotificationBell() {
  const { notifications, unreadCount, refreshUnreadCount, markAsRead } = useNotifications()
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const location = useLocation()

  // Reset count when on notifications page
  useEffect(() => {
    if (location.pathname === '/notifications' || location.pathname === '/received-notifications') {
      refreshUnreadCount()
    }
  }, [location.pathname, refreshUnreadCount])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleClick = () => {
    console.log('🔔 NotificationBell clicked, current state:', dropdownOpen)
    setDropdownOpen(!dropdownOpen)
  }

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      setIsLoading(true)
      await markAsRead(notificationId)
    } catch (error) {
      console.error('Error marking notification as read:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleViewAll = () => {
    try {
      console.log('🔔 Navigating to received-notifications page')
      setDropdownOpen(false)
      navigate('/received-notifications')
    } catch (error) {
      console.error('Error navigating to notifications page:', error)
    }
  }

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60))
    
    if (diffInMinutes < 1) return 'Just now'
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`
    return date.toLocaleDateString()
  }

  const recentNotifications = notifications.slice(0, 5) // Show last 5 notifications

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleClick}
        className="relative p-2 text-gray-400 dark:text-tech-400 hover:text-gray-600 dark:hover:text-tech-200 hover:bg-gray-100/50 dark:hover:bg-tech-800/50 rounded-lg transition-colors"
        title="Notifications"
      >
        <BellIcon className="h-6 w-6" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium animate-pulse">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {dropdownOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-tech-800 rounded-lg shadow-lg border border-gray-200 dark:border-tech-700 z-50">
          <div className="p-4 border-b border-gray-200 dark:border-tech-700">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-tech-100">Notifications</h3>
              <button
                onClick={handleViewAll}
                className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
              >
                View all
              </button>
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {recentNotifications.length === 0 ? (
              <div className="p-4 text-center">
                <BellIcon className="h-8 w-8 text-gray-300 dark:text-tech-600 mx-auto mb-2" />
                <p className="text-sm text-gray-500 dark:text-tech-400">No notifications</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200 dark:divide-tech-700">
                {recentNotifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-4 hover:bg-gray-50 dark:hover:bg-tech-700/50 transition-colors ${
                      !notification.isRead ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className={`text-sm font-medium truncate ${
                            notification.isRead 
                              ? 'text-gray-900 dark:text-tech-100' 
                              : 'text-gray-900 dark:text-tech-100'
                          }`}>
                            {notification.subject}
                          </h4>
                          {!notification.isRead && (
                            <div className="w-2 h-2 bg-primary-500 rounded-full flex-shrink-0"></div>
                          )}
                        </div>
                        
                        <p className="text-xs text-gray-500 dark:text-tech-400 mb-2 line-clamp-2">
                          {notification.message}
                        </p>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center text-xs text-gray-400 dark:text-tech-500">
                            <ClockIcon className="w-3 h-3 mr-1" />
                            {formatTime(notification.sentAt)}
                          </div>
                          
                          {!notification.isRead && (
                            <button
                              onClick={() => handleMarkAsRead(notification.id)}
                              className="p-1 text-gray-400 hover:text-success transition-colors"
                              title="Mark as read"
                            >
                              <CheckCircleIcon className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {recentNotifications.length > 0 && (
            <div className="p-4 border-t border-gray-200 dark:border-tech-700">
              <button
                onClick={handleViewAll}
                className="w-full flex items-center justify-center space-x-2 px-4 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
              >
                <EyeIcon className="w-4 h-4" />
                <span>View all notifications</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
} 